<template>
  <div class="element-highlighter">
    <button class="highlight-toggle" @click="toggleHighlightMode">
      {{ isHighlightMode ? '停用元素高亮' : '啟用元素高亮' }}
    </button>

    <div v-if="currentFeatureKey" class="feature-key-display">
      Feature Key: {{ currentFeatureKey }}
    </div>
  </div>
</template>

<script>
import { ref, onBeforeUnmount, defineComponent } from 'vue'

export default defineComponent({
  name: 'FeatureKeyInspector',
  setup () {
    const isHighlightMode = ref(false)
    const currentHighlightedElement = ref(null)
    const highlightOverlay = ref(null)
    const currentFeatureKey = ref(null)
    const isPaused = ref(false)

    const toggleHighlightMode = () => {
      isHighlightMode.value = !isHighlightMode.value

      if (isHighlightMode.value) {
        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('keyup', handleKeyUp)
      } else {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('keydown', handleKeyDown)
        document.removeEventListener('keyup', handleKeyUp)
        removeHighlight()
        currentFeatureKey.value = null
        isPaused.value = false
      }
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Alt' || event.keyCode === 18) {
        isPaused.value = true
      }
    }

    const handleKeyUp = (event) => {
      if (event.key === 'Alt' || event.keyCode === 18) {
        isPaused.value = false
      }
    }

    const handleMouseMove = (event) => {
      if (isPaused.value) {
        return
      }

      const element = event.target

      if (currentHighlightedElement.value !== element) {
        removeHighlight()
        addHighlight(element)
        findAndSetFeatureKey(element)
      }
    }

    const findAndSetFeatureKey = (element) => {
      let currentElement = element
      currentFeatureKey.value = null

      while (currentElement && currentElement !== document.body) {
        if (currentElement.dataset && currentElement.dataset.featurekey) {
          currentFeatureKey.value = currentElement.dataset.featurekey

          if (currentHighlightedElement.value !== currentElement) {
            removeHighlight()
            addHighlight(currentElement)
          }
          break
        }
        currentElement = currentElement.parentElement
      }
    }

    const addHighlight = (element) => {
      currentHighlightedElement.value = element

      element._originalBorder = element.style.border
      element._originalOutline = element.style.outline
      element._originalPosition = element.style.position

      element.style.outline = '2px solid #58f993'
      element.style.outlineOffset = '-2px'

      if (getComputedStyle(element).position === 'static') {
        element.style.position = 'relative'
      }
    }

    const removeHighlight = () => {
      if (currentHighlightedElement.value) {
        currentHighlightedElement.value.style.border = currentHighlightedElement.value._originalBorder
        currentHighlightedElement.value.style.outline = currentHighlightedElement.value._originalOutline
        currentHighlightedElement.value.style.position = currentHighlightedElement.value._originalPosition

        currentHighlightedElement.value = null
      }
    }

    // Cleanup event listeners when component is destroyed
    onBeforeUnmount(() => {
      if (isHighlightMode.value) {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('keydown', handleKeyDown)
        document.removeEventListener('keyup', handleKeyUp)
      }
    })

    return {
      isHighlightMode,
      currentFeatureKey,
      isPaused,
      toggleHighlightMode,
    }
  },
})
</script>

<style scoped>
.element-highlighter {
  @apply fixed bottom-[50px] right-[20px];
  z-index: 9999;
}

.highlight-toggle {
  background-color: #58f993;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.highlight-toggle:hover {
  background-color: #58f993;
}

.feature-key-display {
  margin-top: 10px;
  padding: 8px 12px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 4px;
  font-size: 14px;
  max-width: 300px;
  overflow-wrap: break-word;
}

.pause-indicator {
  margin-left: 5px;
  font-style: italic;
  color: #ffcc00;
}
</style>
